import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import ModalContext from 'contexts/ModalContext'
import RequestAgentCallbackModal from 'supportAssistant/components/RequestAgentCallback/Modals/RequestAgentCallbackModal'
import ChatPanel from '../components/ChatPanel'
import MessagesList from 'tripPlanner/components/Chat/MessagesList'
import ChatMessageInput from 'tripPlanner/components/Chat/ChatMessageInput'
import AddBookingModal from 'tripPlanner/components/Chat/AddBookingModal'
import DayContextBar from '../components/DayContextBar'
import SalesAgentSummaryPanel from '../components/SalesAgentSummaryPanel'
import ChatFeedbackPopup from '../components/ChatFeedbackPopup'
import { ChatMessageActionsProvider, type OfferClickSearchParams } from '../contexts/ChatMessageActionsContext'
import type { LocationInfoData } from 'tripPlanner/components/Chat/Message'
import { useChatFiles } from '../contexts/ChatFilesContext'
import { InputWrapper } from '../styles/layoutStyles'
import type { HotelMapViewData, ExperienceMapViewData } from 'tripPlanner/api/types'
import { useClipboard } from '../hooks/useClipboard'
import { useFeedback } from '../hooks/useFeedback'
import { getChatErrorMessage, getChatErrorHeading } from '../utils/chatErrorMessages'
import { formatDayLabel, getLocalisedDayLabel } from 'tripPlanner/utils/chatDates'
import { useTripPlannerEventBus, useEventSubscription } from '../contexts/TripPlannerEventBus'
import { useChatActions } from '../contexts/ChatActionsContext'
import { useChatStreamStatus } from '../contexts/ChatStreamStatusContext'
import { useChatScrollContext } from '../contexts/ChatScrollContext'
import { useChatStreamMessages } from '../contexts/ChatStreamMessagesContext'
import { useItineraryContext } from '../contexts/ItineraryContext'
import { useMapInteraction } from '../contexts/MapInteractionContext'
import { useChatFeedbackPopup } from '../hooks/useChatFeedbackPopup'
import ErrorBoundary from 'components/Common/ErrorBoundary'
import BodyText from 'LuxKit/Typography/BodyText'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { INTL_DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import type { MappedRequestAgentCallbackResult } from 'supportAssistant/components/RequestAgentCallback/Modals/RequestAgentCallbackModal'
import { formatCallbackTimeLabel } from 'supportAssistant/components/RequestAgentCallback/RequestAgentCallbackUtils'
import { shouldDefaultSalesSummaryCollapsed } from '../utils/salesAgentSummary'

interface Props {
  chatId: number | undefined
  isDevMode: boolean
  isSpoofed: boolean
  spoofingAgentEmail?: string
  navigateToNewChatRoute: () => void
  handleSubmit: (message: string) => Promise<void>
  handleMultiChoiceSelectionChange: (selection: Array<string>) => void
  activeMultiChoiceSelection: Array<string>
  mobileSelectionText: string | undefined
  offerContextTag: { label: string; offerId: string } | undefined
  handleRemoveTag: (tag: string) => void
  selectedBookingItemIds?: Array<string>
  onBookingItemsAdded?: (bookingItemIds: Array<string>) => void
  chatInputRef: React.RefObject<HTMLTextAreaElement | null>
}

const DEFAULT_SELECTED_BOOKING_ITEM_IDS: Array<string> = []

function ChatContainer({
  chatId,
  isDevMode,
  isSpoofed,
  spoofingAgentEmail,
  navigateToNewChatRoute,
  handleSubmit,
  handleMultiChoiceSelectionChange,
  activeMultiChoiceSelection,
  mobileSelectionText,
  offerContextTag,
  handleRemoveTag,
  selectedBookingItemIds = DEFAULT_SELECTED_BOOKING_ITEM_IDS,
  onBookingItemsAdded,
  chatInputRef,
}: Props) {
  const intl = useIntl()
  const intlDateFormatter = useIntlDateFormatter()
  const eventBus = useTripPlannerEventBus()
  const showModal = useContext(ModalContext)
  const { onCardHover } = useMapInteraction()
  const { files, setFiles } = useChatFiles()

  const { stopStreaming, regenerateResponse: streamingRegenerate, sendPostAuthMessage, setFollowTail, submitMessage } = useChatActions()
  const { isStreaming, isStreamOpen, loadingState, isStalled, isOnline, reasoningSummaries, toolCallInfo } = useChatStreamStatus()
  const { handleScroll: handleMessagesScroll, isAtBottom, hasContentOutOfView, enableFollowAndScrollToBottom, dynamicSpacerHeight, messagesWrapperRef } = useChatScrollContext()

  const {
    messages,
    filteredMessages,
    isChatError,
    chatError,
    userFeedbackMap,
    setUserFeedbackMap,
  } = useChatStreamMessages()

  const { itineraryData } = useItineraryContext()

  // ── Local state ──

  const [messageInputValue, setMessageInputValue] = useState('')
  const [dayContext, setDayContext] = useState<{ dayNumber: number; title: string; date?: string } | undefined>(undefined)
  const historyOverlayTouchStartYRef = useRef<number | undefined>(undefined)

  // ── Derived values ──

  const isLoading = loadingState.status === 'loadingHistory' || loadingState.status === 'preparingStream'
  const isListLoading = loadingState.isListLoading
  const isUpdatingItinerary = loadingState.isUpdatingItinerary
  const activeToolLoadingKey = loadingState.activeToolLoadingKey
  const shouldShowSalesSummary = isSpoofed && chatId !== undefined
  const shouldStartSalesSummaryCollapsed = shouldDefaultSalesSummaryCollapsed(spoofingAgentEmail)

  const messagesWithoutInlineMap = useMemo(() => {
    return filteredMessages.map(msg => ({
      ...msg,
      parts: msg.parts.filter(part => {
        if (part.type !== 'ui_component') return true
        const data = part.data
        if (data && typeof data === 'object' && 'type' in data) {
          return data.type !== 'location_entities'
        }
        return true
      }),
    }))
  }, [filteredMessages])

  const lastAssistantMessage = useMemo(() => {
    return filteredMessages.findLast(m => m.role === 'assistant')
  }, [filteredMessages])

  const hasUserMessage = useMemo(() => {
    return filteredMessages.some(m => m.role === 'user')
  }, [filteredMessages])

  const lastMessageIsAssistant = filteredMessages.length > 0 && filteredMessages[filteredMessages.length - 1].role === 'assistant'
  const shouldShowAssistantActions = !!lastAssistantMessage && lastMessageIsAssistant && !isLoading && !isStreaming

  const hasMultiChoiceSelection = activeMultiChoiceSelection.length > 0
  const hasMobileSelection = mobileSelectionText !== undefined
  const hasFiles = files.length > 0
  const hasMessageContent = messageInputValue.trim() !== '' || hasMultiChoiceSelection || hasMobileSelection || hasFiles
  const showStopButton = isStreaming || isStreamOpen
  const disableSubmit = isLoading || showStopButton || !hasMessageContent

  const allSelectedTags = useMemo(() => {
    const tags = [...activeMultiChoiceSelection]
    if (mobileSelectionText) {
      tags.push(mobileSelectionText)
    }
    if (offerContextTag) {
      tags.push(offerContextTag.label)
    }
    return tags
  }, [activeMultiChoiceSelection, mobileSelectionText, offerContextTag])

  // ── Chat error effect ──

  useEffect(() => {
    if (isChatError && chatId !== undefined) {
      const errorStatus = chatError && typeof chatError === 'object' && 'status' in chatError && typeof chatError.status === 'number'
        ? chatError.status
        : undefined
      showSnackbar(
        getChatErrorMessage(intl, errorStatus),
        'critical',
        { heading: getChatErrorHeading(intl) },
      )
      navigateToNewChatRoute()
    }
  }, [isChatError, chatId, chatError, navigateToNewChatRoute, intl])

  // ── Hooks ──

  const { copiedRecently, handleCopyResponse } = useClipboard({ lastAssistantMessage, isDevMode })

  const handleFeedbackSubmitted = useCallback((messageId: string, reaction: 'UP' | 'DOWN') => {
    setUserFeedbackMap(prev => ({
      ...prev,
      [messageId]: reaction,
    }))
  }, [setUserFeedbackMap])

  const { feedbackSubmitted, handleThumbsUp, handleThumbsDown } = useFeedback({
    chatId,
    lastAssistantMessageId: lastAssistantMessage?.id,
    existingFeedback: userFeedbackMap,
    onFeedbackSubmitted: handleFeedbackSubmitted,
  })
  const feedbackPopup = useChatFeedbackPopup({ chatId, messages })

  // ── Callbacks ──

  const focusChatInput = useCallback(() => {
    chatInputRef.current?.focus()
  }, [chatInputRef])

  const handleClearDayContext = useCallback(() => {
    setDayContext(undefined)
  }, [])

  const handleRegenerateResponse = useCallback(async() => {
    if (isLoading) return
    if (!lastAssistantMessage) return
    const lastUserMessage = messages.findLast((message) => message.role === 'user')
    if (!lastUserMessage) return
    setFollowTail(false)
    return streamingRegenerate()
  }, [isLoading, lastAssistantMessage, messages, streamingRegenerate, setFollowTail])

  const handleCallbackRequestSubmitted = useCallback(async(results: MappedRequestAgentCallbackResult) => {
    const { callbackDate, callbackTime } = results.customer
    const formattedTime = formatCallbackTimeLabel(callbackTime, intlDateFormatter)
    const formattedDate = callbackDate
      ? intlDateFormatter(callbackDate, INTL_DMY_CASUAL_FORMAT)
      : ''

    if (!formattedTime || !formattedDate) {
      return
    }

    await submitMessage(
      intl.formatMessage(
        {
          id: '1JIZSmwFqLHg37YN0HnK',
          defaultMessage: 'I scheduled a callback for {time} on {date}.',
          description: 'User chat message sent automatically after successfully scheduling a callback in trip planner chat',
        },
        {
          time: formattedTime,
          date: formattedDate,
        },
      ),
    )
  }, [intl, intlDateFormatter, submitMessage])

  const handleScheduleCallback = useCallback(() => {
    showModal(
      <RequestAgentCallbackModal
        type="SALES"
        onRequestSubmitted={handleCallbackRequestSubmitted}
        messagePlaceholder={intl.formatMessage({
          id: 'i-4TtHQw-VV2kmnT-QDB',
          defaultMessage: "e.g. I'd love help turning my itinerary into a real trip",
          description: 'Custom message placeholder shown in the request callback modal when opened from trip planner chat',
        })}
      />,
    )
  }, [handleCallbackRequestSubmitted, intl, showModal])

  const handleSubmitWithDayContext = useCallback((message: string) => {
    if (dayContext) {
      const dayLabel = dayContext.date
        ? `${formatDayLabel(dayContext.date)} (${getLocalisedDayLabel(intl, dayContext.dayNumber)})`
        : getLocalisedDayLabel(intl, dayContext.dayNumber)
      const messageWithContext = `${message}\n<Context label="${dayLabel} - ${dayContext.title}">The user is asking about ${dayLabel}: ${dayContext.title}</Context>`
      setDayContext(undefined)
      return handleSubmit(messageWithContext)
    }
    return handleSubmit(message)
  }, [dayContext, handleSubmit, intl])

  const handleAddBookingClick = useCallback(() => {
    showModal(
      <AddBookingModal
        onSendMessage={handleSubmitWithDayContext}
        disabledBookingItemIds={selectedBookingItemIds}
        onBookingItemsAdded={onBookingItemsAdded}
        isSubmitLocked={isStreaming}
        chatId={chatId}
      />,
    )
  }, [chatId, handleSubmitWithDayContext, isStreaming, onBookingItemsAdded, selectedBookingItemIds, showModal])

  // ── History overlay scroll forwarding ──

  const handleHistoryOverlayWheel = useCallback((event: React.WheelEvent<HTMLDivElement>) => {
    if (!messagesWrapperRef.current) {
      return
    }
    event.preventDefault()
    messagesWrapperRef.current.scrollTop += event.deltaY
  }, [messagesWrapperRef])

  const handleHistoryOverlayTouchStart = useCallback((event: React.TouchEvent<HTMLDivElement>) => {
    const firstTouch = event.touches[0]
    if (!firstTouch) {
      return
    }
    historyOverlayTouchStartYRef.current = firstTouch.clientY
  }, [])

  const handleHistoryOverlayTouchMove = useCallback((event: React.TouchEvent<HTMLDivElement>) => {
    if (!messagesWrapperRef.current || historyOverlayTouchStartYRef.current === undefined) {
      return
    }

    const touch = event.touches[0]
    if (!touch) {
      return
    }

    const deltaY = historyOverlayTouchStartYRef.current - touch.clientY
    if (deltaY !== 0) {
      messagesWrapperRef.current.scrollTop += deltaY
      historyOverlayTouchStartYRef.current = touch.clientY
    }
    event.preventDefault()
  }, [messagesWrapperRef])

  const handleHistoryOverlayTouchEnd = useCallback(() => {
    historyOverlayTouchStartYRef.current = undefined
  }, [])

  // ── ChatMessageActionsProvider callbacks ──

  const handleHotelMapViewClick = useCallback((data: HotelMapViewData) => {
    eventBus.dispatch({ type: 'show_hotel_on_map', data })
  }, [eventBus])

  const handleExperienceMapViewClick = useCallback((data: ExperienceMapViewData) => {
    eventBus.dispatch({ type: 'show_experience_on_map', data })
  }, [eventBus])

  const handleOfferClick = useCallback((data: { offerId: string; searchParams?: OfferClickSearchParams; offerType?: string }) => {
    eventBus.dispatch({ type: 'open_offer_drawer', ...data })
  }, [eventBus])

  const handlePlaceClick = useCallback((data: { googlePlaceId: string; placeName: string }) => {
    eventBus.dispatch({ type: 'open_place_drawer', ...data })
  }, [eventBus])

  const handleLocationInfoClick = useCallback((data: LocationInfoData) => {
    eventBus.dispatch({ type: 'open_location_drawer', data })
  }, [eventBus])

  // ── Event bus subscriptions ──

  useEventSubscription('ask_ai_for_day', (event) => {
    setDayContext({ dayNumber: event.dayNumber, title: event.title, date: event.date })
    focusChatInput()
  })

  useEventSubscription('reset_chat_state', () => {
    setMessageInputValue('')
    setDayContext(undefined)
  })

  // ── i18n ──

  const askAboutDayPlaceholder = dayContext
    ? intl.formatMessage(
        {
          id: '7eTuFAD860P0YRHQhFcJ',
          defaultMessage: 'Ask me anything about {dayLabel}',
          description: 'Chat input placeholder when user is asking about a specific day in the trip itinerary',
        },
        { dayLabel: getLocalisedDayLabel(intl, dayContext.dayNumber, dayContext.date) },
      )
    : undefined
  const chatInputPlaceholder = askAboutDayPlaceholder
  const messagesErrorText = intl.formatMessage({
    id: 'zNUAhjKCUvuU-8zm4DQm',
    defaultMessage: 'Something went wrong displaying messages.',
    description: 'Fallback error message shown if trip planner chat messages fail to render',
  })

  // ── Render ──

  const inputContent = (
    <InputWrapper>
      {feedbackPopup.isVisible && (
        <ChatFeedbackPopup
          onClose={feedbackPopup.dismiss}
          onSubmit={feedbackPopup.submit}
        />
      )}
      {dayContext && (
        <DayContextBar
          dayNumber={dayContext.dayNumber}
          title={dayContext.title}
          date={dayContext.date}
          onClose={handleClearDayContext}
        />
      )}
      <ChatMessageInput
        onSubmit={handleSubmitWithDayContext}
        value={messageInputValue}
        onChange={setMessageInputValue}
        disableSubmit={disableSubmit}
        isStreaming={isStreaming}
        showStopButton={showStopButton}
        onStopStream={stopStreaming}
        files={files}
        onFilesChange={setFiles}
        selectedTags={allSelectedTags}
        onAddBookingClick={handleAddBookingClick}
        onRemoveTag={handleRemoveTag}
        inputRef={chatInputRef}
        placeholder={chatInputPlaceholder}
        chatId={chatId}
      />
    </InputWrapper>
  )

  return (
    <ChatMessageActionsProvider
      onMultiChoiceSelectionChange={handleMultiChoiceSelectionChange}
      onOfferClick={handleOfferClick}
      onOfferHover={onCardHover}
      onHotelMapViewClick={handleHotelMapViewClick}
      onExperienceMapViewClick={handleExperienceMapViewClick}
      onPlaceClick={handlePlaceClick}
      onLocationInfoClick={handleLocationInfoClick}
      onScheduleCallback={handleScheduleCallback}
      onSendMessage={handleSubmit}
      onAuthenticatedFollowUp={sendPostAuthMessage}
      itineraryData={itineraryData}
      isDevMode={isDevMode}
    >
      <ChatPanel
        messagesWrapperRef={messagesWrapperRef}
        onScroll={handleMessagesScroll}
        isOverlay={false}
        showHistoryOverlay={false}
        onHistoryWheel={handleHistoryOverlayWheel}
        onHistoryTouchStart={handleHistoryOverlayTouchStart}
        onHistoryTouchMove={handleHistoryOverlayTouchMove}
        onHistoryTouchEnd={handleHistoryOverlayTouchEnd}
        inputSlot={inputContent}
        isAtBottom={isAtBottom}
        hasContentOutOfView={hasContentOutOfView}
        onScrollToBottomButtonClick={enableFollowAndScrollToBottom}
        dynamicSpacerHeight={dynamicSpacerHeight}
        topSlot={
          <SalesAgentSummaryPanel
            chatId={chatId}
            enabled={shouldShowSalesSummary}
            initiallyCollapsed={shouldStartSalesSummaryCollapsed}
          />
        }
      >
        <ErrorBoundary fallback={() => <BodyText variant="medium" colour="critical">{messagesErrorText}</BodyText>}>
          <MessagesList
            messages={messagesWithoutInlineMap}
            isLoading={isListLoading}
            isUpdatingItinerary={isUpdatingItinerary}
            activeToolLoadingKey={activeToolLoadingKey}
            isStalled={isStalled}
            isOnline={isOnline}
            reasoningSummaries={reasoningSummaries}
            toolCallInfo={toolCallInfo}
            loadingStatus={loadingState.status}
            showAssistantActions={shouldShowAssistantActions}
            onRegenerate={handleRegenerateResponse}
            onCopy={handleCopyResponse}
            disableRegenerateButton={isLoading || !hasUserMessage}
            disableCopyButton={!lastAssistantMessage || isLoading}
            copiedRecently={copiedRecently}
            onThumbsUp={handleThumbsUp}
            onThumbsDown={handleThumbsDown}
            feedbackSubmitted={feedbackSubmitted}
            externalSelection={activeMultiChoiceSelection}
          />
        </ErrorBoundary>
      </ChatPanel>
    </ChatMessageActionsProvider>
  )
}

export default ChatContainer
